import { useEffect, useCallback, useRef } from 'react';
import { useOfflineDetection } from './useOfflineDetection';
import { useSyncQueue } from './useSyncQueue';
import { 
  isBackgroundSyncSupported, 
  registerBackgroundSync, 
  listenForSyncComplete 
} from '@/lib/backgroundSync';

/**
 * Hook that manages background sync registration and handling
 * Automatically registers for background sync when offline edits occur
 * and listens for sync completion from the service worker
 */
export const useBackgroundSync = () => {
  const { isOnline } = useOfflineDetection();
  const { pendingCount, processSyncQueue, checkPendingCount } = useSyncQueue();
  const wasOffline = useRef(false);
  const syncRegistered = useRef(false);

  // Register background sync when we have pending changes and go offline
  const registerSync = useCallback(async () => {
    if (!isOnline && pendingCount > 0 && !syncRegistered.current) {
      const success = await registerBackgroundSync();
      if (success) {
        syncRegistered.current = true;
        console.log('Background sync registered for', pendingCount, 'pending changes');
      }
    }
  }, [isOnline, pendingCount]);

  // Handle coming back online
  useEffect(() => {
    if (wasOffline.current && isOnline) {
      // We just came back online
      console.log('Back online, checking for pending sync');
      checkPendingCount();
      syncRegistered.current = false;
      
      // If background sync is not supported, trigger manual sync
      if (!isBackgroundSyncSupported() && pendingCount > 0) {
        console.log('Background sync not supported, triggering manual sync');
        processSyncQueue();
      }
    }
    wasOffline.current = !isOnline;
  }, [isOnline, pendingCount, processSyncQueue, checkPendingCount]);

  // Register sync when going offline with pending changes
  useEffect(() => {
    if (!isOnline && pendingCount > 0) {
      registerSync();
    }
  }, [isOnline, pendingCount, registerSync]);

  // Listen for sync complete messages from service worker
  useEffect(() => {
    const cleanup = listenForSyncComplete((success) => {
      console.log('Sync complete from service worker:', success);
      if (success) {
        checkPendingCount();
        syncRegistered.current = false;
      }
    });

    return cleanup;
  }, [checkPendingCount]);

  return {
    isBackgroundSyncSupported: isBackgroundSyncSupported(),
    registerSync,
    pendingCount
  };
};
